import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  Box,
  Boxes,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  HardDrive,
  KeyRound,
  ListChecks,
  LogIn,
  Monitor,
  Network,
  Power,
  Server,
  Shield,
  Terminal,
  Usb,
  UserCheck,
  Users,
} from 'lucide-react';
import Header from '../layout/Header';

type SectionId =
  | 'prerequis'
  | 'ubuntu-download'
  | 'usb'
  | 'bios-power-on'
  | 'install-gmktec'
  | 'install-options'
  | 'first-login'
  | 'admin'
  | 'update'
  | 'tailscale'
  | 'tailscale-login'
  | 'incus-install'
  | 'incus-config'
  | 'incus-containers'
  | 'incus-list'
  | 'incus-base-tools'
  | 'incus-enter';

type CommandExample = {
  label?: string;
  command: string;
  /** Parties du texte à afficher en jaune (ex. noms de conteneurs à adapter). */
  highlightNames?: string[];
};

type ProcedureStep = {
  id: SectionId;
  number: string;
  label: string;
  shortLabel: string;
  icon: typeof Shield;
  intro?: string;
  note?: string;
  links?: { label: string; href: string }[];
  items: string[];
  commands?: Array<string | CommandExample>;
  /** Alerte affichée entre le titre « Commandes » et le premier exemple. */
  commandsCallout?: string;
  /** Aperçu terminal du résultat attendu. */
  exampleOutput?: string;
  expected?: string;
};

const INCUS_PRESEED_COMMAND = `incus admin init --preseed <<'EOF'
config: {}

networks:
  - config:
      ipv4.address: 10.103.3.1/24
      ipv4.nat: "true"
      ipv6.address: none
    description: ""
    name: incusbr0
    type: bridge

storage_pools:
  - config:
      size: 350GiB
    description: ""
    name: default
    driver: btrfs

profiles:
  - config: {}
    description: Profil Incus par défaut
    devices:
      eth0:
        name: eth0
        network: incusbr0
        type: nic
      root:
        path: /
        pool: default
        size: 50GiB
        type: disk
    name: default

projects: []
cluster: null
EOF`;

const sections: ProcedureStep[] = [
  {
    id: 'prerequis',
    number: '0',
    label: 'Prérequis (comptes)',
    shortLabel: 'Prérequis',
    icon: Users,
    intro: 'À faire avant de toucher au serveur. Fais ces 4 choses sur ton PC (pas sur le serveur).',
    items: [
      'Créer une adresse courriel via Microsoft 365 : bzinc.ai',
      'Créer un compte GitHub lié à ton courriel bzinc.ai (si tu en as déjà un, garde-le).',
      'Créer un compte Telegram (si tu en as déjà un, garde-le).',
      'Créer un compte Tailscale et lier le login avec GitHub.',
    ],
    links: [
      { label: 'GitHub', href: 'https://github.com/' },
      { label: 'Telegram Web', href: 'https://web.telegram.org/k/' },
      { label: 'Tailscale', href: 'https://tailscale.com/' },
    ],
  },
  {
    id: 'ubuntu-download',
    number: '1',
    label: 'Télécharger Ubuntu Server (LTS)',
    shortLabel: 'Ubuntu Server',
    icon: Server,
    intro: 'Télécharger la dernière version LTS de Ubuntu Server.',
    items: ['Choisir la dernière version LTS (Long Term Support).'],
    links: [
      {
        label: 'Tutoriel Ubuntu Server',
        href: 'https://ubuntu.com/tutorials/install-ubuntu-server#1-overview',
      },
    ],
  },
  {
    id: 'usb',
    number: '2',
    label: 'Mettre l’ISO sur une clé USB',
    shortLabel: 'Clé USB',
    icon: Usb,
    intro: 'Transférer l’ISO sur une clé USB (ex. avec PowerISO).',
    items: ['Créer une clé USB bootable à partir de l’ISO Ubuntu Server.'],
    expected: 'Une clé USB bootable prête à démarrer le GMKtec.',
    links: [{ label: 'Télécharger PowerISO', href: 'https://www.poweriso.com/download.htm' }],
  },
  {
    id: 'bios-power-on',
    number: '3',
    label: 'BIOS — activer Power On',
    shortLabel: 'BIOS Power On',
    icon: Power,
    intro:
      'Avant d’installer Ubuntu, entre dans le BIOS du poste pour activer le redémarrage automatique après une panne de courant.',
    note:
      'Objectif : s’il manque d’électricité, au retour du courant le poste redémarre automatiquement (AC Power Recovery / Restore on AC Power Loss / Power On After Power Fail).',
    items: [
      'Démarrer le GMKtec et entrer dans le BIOS (souvent Del, F2 ou Escape — selon le modèle).',
      'Chercher l’option Power On / Restore on AC Power Loss / After Power Loss.',
      'Activer le Power On (souvent « Power On » ou « Last State » selon le BIOS).',
      'Sauvegarder et quitter le BIOS (Save & Exit).',
    ],
    expected:
      'Après une coupure électrique, le poste redémarre tout seul au retour du courant.',
  },
  {
    id: 'install-gmktec',
    number: '4',
    label: 'Installer Ubuntu sur le GMKtec',
    shortLabel: 'Installation GMKtec',
    icon: HardDrive,
    items: [
      'Brancher la clé USB sur le GMKtec.',
      'Démarrer le GMKtec et ouvrir le Boot Menu avec F7.',
      'Choisir la clé USB, puis lancer l’installation.',
    ],
  },
  {
    id: 'install-options',
    number: '5',
    label: 'Options pendant l’installation',
    shortLabel: 'Options installation',
    icon: Monitor,
    intro: 'IMPORTANT — pendant l’installation, choisis exactement ceci (ou le plus proche).',
    note: 'À la fin : retire la clé USB et reboot.',
    items: [
      'Langue : English',
      'Type : ubuntu server',
      'Option : search for third-party drivers',
      'Network : IPv4 activé',
      'Proxy : laisser vide',
      'Guided storage : default',
      'Profile configuration : Your name / Your server’s name / pick a username — mets la même chose',
      'Password : mets ton mot de passe',
      'Ubuntu Pro : skip for now',
      'SSH configuration : install OpenSSH server',
      'Featured server snaps : ne rien sélectionner',
    ],
  },
  {
    id: 'first-login',
    number: '6',
    label: 'Première connexion',
    shortLabel: 'Première connexion',
    icon: UserCheck,
    items: ['Connecte-toi avec ton username et ton password.'],
  },
  {
    id: 'admin',
    number: '7',
    label: 'Passer en admin',
    shortLabel: 'Passer en admin',
    icon: KeyRound,
    intro: 'Entre ton mot de passe quand demandé.',
    items: ['Passer en superutilisateur pour les prochaines commandes.'],
    commands: ['sudo su'],
  },
  {
    id: 'update',
    number: '8',
    label: 'Mettre Ubuntu à jour',
    shortLabel: 'Mises à jour',
    icon: Terminal,
    intro: 'Fais les commandes dans l’ordre.',
    items: ['Mettre à jour les index de paquets.', 'Appliquer les mises à niveau disponibles.'],
    commands: ['apt update', 'apt upgrade'],
  },
  {
    id: 'tailscale',
    number: '9',
    label: 'Installer Tailscale',
    shortLabel: 'Installer Tailscale',
    icon: Network,
    intro: 'Sur le serveur Ubuntu, lance le script d’installation officiel.',
    items: ['Installer Tailscale via le script Linux officiel.'],
    commands: ['curl -fsSL https://tailscale.com/install.sh | sh'],
    links: [{ label: 'Docs Tailscale Linux', href: 'https://tailscale.com/docs/install/linux' }],
  },
  {
    id: 'tailscale-login',
    number: '9.1',
    label: 'Login Tailscale',
    shortLabel: 'Login Tailscale',
    icon: Shield,
    intro: 'Après l’installation, Tailscale va te donner une URL.',
    items: [
      'Lancer `sudo tailscale up`.',
      'Copier l’URL affichée.',
      'Ouvrir l’URL sur ton PC.',
      'Te connecter avec ton compte Tailscale (lié à GitHub).',
      'Ensuite, activer SSH Tailscale avec le tag OpenClaw.',
    ],
    commands: [
      'sudo tailscale up',
      'sudo tailscale up --ssh --advertise-tags=tag:openclaw',
    ],
  },
  {
    id: 'incus-install',
    number: '10',
    label: 'Installation d’Incus',
    shortLabel: 'Installer Incus',
    icon: Box,
    intro:
      'Incus permet de créer des conteneurs isolés pour chacun des agents OpenClaw sur ce poste.',
    items: [
      'Mettre à jour les paquets.',
      'Installer Incus.',
    ],
    commands: ['apt update', 'apt install -y incus'],
  },
  {
    id: 'incus-config',
    number: '11',
    label: 'Configuration d’Incus',
    shortLabel: 'Configurer Incus',
    icon: Boxes,
    intro:
      'Configure automatiquement Incus avec un pool total de 350 Gio et 50 Gio par conteneur (profil default).',
    items: [
      'Réseau bridge : incusbr0 (10.103.3.1/24, NAT IPv4).',
      'Storage pool default : 350GiB (driver btrfs).',
      'Profil default : disque root limité à 50GiB par conteneur.',
      'Coller et exécuter la commande preseed complète ci-dessous.',
    ],
    commands: [INCUS_PRESEED_COMMAND],
    expected: 'Incus initialisé : pool 350GiB, bridge Incus, profil default à 50GiB/conteneur.',
  },
  {
    id: 'incus-containers',
    number: '12',
    label: 'Création des conteneurs',
    shortLabel: 'Créer conteneurs',
    icon: Boxes,
    intro:
      'Crée un conteneur Ubuntu 24.04 par agent. Le préfixe est le nom du client, suivi du type d’agent et du nom de l’agent.',
    note:
      'Convention : {nom-du-client}-{type-d’agent}-{nom-de-l’agent}. Types possibles : hermes, openclaw, autre. Exemple à adapter : bz-hermes-novia. Minuscules et tirets seulement.',
    items: [
      'Format du nom : nom du client + type d’agent + nom de l’agent (ex. bz-hermes-novia).',
      'Types d’agent possibles : hermes, openclaw, ou autre selon le mandat.',
      'Exemple 1 conteneur ou 3 conteneurs ci-dessous — choisis selon le besoin.',
    ],
    commandsCallout:
      'N’oublie pas de changer le nom du conteneur avant d’exécuter la commande.',
    commands: [
      {
        label: 'Exemple — 1 conteneur',
        command: 'incus launch images:ubuntu/24.04 bz-hermes-novia',
        highlightNames: ['bz-hermes-novia'],
      },
      {
        label: 'Exemple — 3 conteneurs',
        command:
          'incus launch images:ubuntu/24.04 bz-hermes-novia\nincus launch images:ubuntu/24.04 bz-hermes-tony\nincus launch images:ubuntu/24.04 bz-hermes-cyberta',
        highlightNames: ['bz-hermes-novia', 'bz-hermes-tony', 'bz-hermes-cyberta'],
      },
    ],
    expected: 'Un conteneur Ubuntu 24.04 créé et démarré pour chaque agent.',
  },
  {
    id: 'incus-list',
    number: '13',
    label: 'Vérifier les conteneurs',
    shortLabel: 'incus list',
    icon: ListChecks,
    intro:
      'Après la création, assure-toi que tous les conteneurs sont bien créés et en cours d’exécution.',
    items: [
      'Lancer `incus list`.',
      'Vérifier que chaque conteneur apparaît avec l’état RUNNING.',
      'Les noms, IP et snapshots peuvent différer — le format du tableau doit ressembler à l’exemple ci-dessous.',
    ],
    commands: ['incus list'],
    exampleOutput: `+-----------------+---------+-----------------------+-----------------------------------------------+-----------+-----------+
|      NAME       |  STATE  |         IPV4          |                     IPV6                      |   TYPE    | SNAPSHOTS |
+-----------------+---------+-----------------------+-----------------------------------------------+-----------+-----------+
| hermes-cyberta  | RUNNING | 100.97.16.54 (tailscale0) | fd7a:115c:a1e0::bc3b:1037 (tailscale0) | CONTAINER | 1         |
|                 |         | 10.72.146.138 (eth0)  |                                               |           |           |
+-----------------+---------+-----------------------+-----------------------------------------------+-----------+-----------+
| hermes-lola     | RUNNING | 100.74.154.29 (tailscale0) | fd7a:115c:a1e0::c336:9a1e (tailscale0) | CONTAINER | 1         |
|                 |         | 10.72.146.94 (eth0)   |                                               |           |           |
+-----------------+---------+-----------------------+-----------------------------------------------+-----------+-----------+
| hermes-tony     | RUNNING | 100.78.188.49 (tailscale0) | fd7a:115c:a1e0::9139:bc32 (tailscale0) | CONTAINER | 0         |
|                 |         | 10.72.146.232 (eth0)  |                                               |           |           |
+-----------------+---------+-----------------------+-----------------------------------------------+-----------+-----------+`,
    expected: 'Tous tes conteneurs apparaissent dans la liste, en état RUNNING.',
  },
  {
    id: 'incus-base-tools',
    number: '14',
    label: 'Outils de base dans les conteneurs',
    shortLabel: 'Outils de base',
    icon: Terminal,
    intro:
      'Installe les outils de base et OpenSSH dans chacun des conteneurs, puis prépare la connexion SSH avec le compte root.',
    note:
      'Adapte la liste des noms dans la boucle `for c in ...` selon tes conteneurs réels (incus list). Cette étape ne change pas le mot de passe root : si le compte est verrouillé, définis son mot de passe plus tard avec `incus exec NOM_DU_CONTENEUR -- passwd root`.',
    items: [
      'Remplacer les noms d’exemple par tes vrais conteneurs.',
      'La boucle installe les outils et OpenSSH dans chaque conteneur via `incus exec`.',
      'La connexion SSH par mot de passe est autorisée pour root, sans modifier son mot de passe.',
      'Attendre la fin de l’installation et vérifier que SSH écoute sur le port 22 dans tous les conteneurs.',
    ],
    commandsCallout:
      'N’oublie pas de changer les noms des conteneurs dans la boucle avant d’exécuter.',
    commands: [
      {
        label: 'Exemple — 3 conteneurs',
        command: `for c in bz-hermes-novia bz-hermes-tony bz-hermes-cyberta; do
    echo "=== Configuration de $c ==="

    incus exec "$c" -- bash -c '
        set -e

        export DEBIAN_FRONTEND=noninteractive

        apt update
        apt install -y \\
            openssh-server \\
            nano vim git curl wget xz-utils htop btop tree jq zip unzip \\
            net-tools dnsutils iputils-ping traceroute rsync tmux screen

        install -d -m 0755 /etc/ssh/sshd_config.d

        printf "%s\\n" \\
            "PermitRootLogin yes" \\
            "PasswordAuthentication yes" \\
            > /etc/ssh/sshd_config.d/00-hermes-root-login.conf

        sshd -t
        systemctl enable --now ssh
        systemctl restart ssh

        echo "--- Vérification SSH ---"
        systemctl is-active ssh
        sshd -T | grep -Ei "^(permitrootlogin|passwordauthentication) "
        passwd -S root
        ss -lntp | grep ":22 "
    '
done`,
        highlightNames: ['bz-hermes-novia', 'bz-hermes-tony', 'bz-hermes-cyberta'],
      },
    ],
    expected:
      'Chaque conteneur a les outils de base et OpenSSH installés; le service SSH est actif sur le port 22 et la connexion root par mot de passe est autorisée.',
  },
  {
    id: 'incus-enter',
    number: '15',
    label: 'Entrer dans un conteneur',
    shortLabel: 'Entrer dans Incus',
    icon: LogIn,
    intro:
      'Pour travailler à l’intérieur d’un conteneur (shell interactif), utilise `incus exec` avec bash.',
    note: 'Pour sortir du conteneur : tape `exit` ou Ctrl+D.',
    items: [
      'Remplacer le nom d’exemple par le conteneur voulu (`incus list`).',
      'Tu obtiens un shell root dans le conteneur.',
      'Toutes les commandes suivantes s’exécutent dans ce conteneur jusqu’à ce que tu sortes.',
    ],
    commandsCallout:
      'N’oublie pas de changer le nom du conteneur avant d’exécuter la commande.',
    commands: [
      {
        label: 'Entrer dans un conteneur',
        command: 'incus exec bz-hermes-novia -- bash',
        highlightNames: ['bz-hermes-novia'],
      },
    ],
    expected: 'Prompt shell à l’intérieur du conteneur (ex. root@bz-hermes-novia).',
  },
];

function normalizeCommand(entry: string | CommandExample): CommandExample {
  return typeof entry === 'string' ? { command: entry } : entry;
}

function renderHighlightedCommand(command: string, highlightNames: string[] = []) {
  if (!highlightNames.length) return command;

  const escaped = highlightNames
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'g');

  return command.split(regex).map((part, index) =>
    highlightNames.includes(part) ? (
      <span key={`${part}-${index}`} className="font-black text-amber-300">
        {part}
      </span>
    ) : (
      <span key={`${part}-${index}`}>{part}</span>
    ),
  );
}

function CommandBlock({ label, command, highlightNames }: CommandExample) {
  const [copied, setCopied] = useState(false);

  const copyCommand = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = command;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }, [command]);

  return (
    <div className="space-y-2">
      {label ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-300">{label}</p>
      ) : null}
      <div className="flex items-stretch gap-2 rounded-2xl border border-emerald-400/20 bg-emerald-950/40">
        <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-all px-4 py-3 font-mono text-sm text-emerald-100">
          {renderHighlightedCommand(command, highlightNames)}
        </pre>
        <button
          type="button"
          onClick={() => void copyCommand()}
          className={`m-1.5 inline-flex shrink-0 items-center gap-1.5 self-start rounded-xl border px-3 py-2 text-xs font-bold transition ${
            copied
              ? 'border-emerald-300/40 bg-emerald-500/30 text-emerald-50'
              : 'border-emerald-400/25 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25'
          }`}
          aria-label="Copier la commande"
          title="Copier la commande"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
    </div>
  );
}

export default function OpenClawInstallationProcedure() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<SectionId>('prerequis');

  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeId) ?? sections[0],
    [activeId],
  );

  useEffect(() => {
    const el = document.getElementById(`section-${activeId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeId]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header
        title="Installation OpenClaw"
        subtitle="Procédure pas à pas — Ubuntu, Tailscale, Incus et outils de base"
      />

      <div className="border-b border-indigo-500/20 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
          <button
            type="button"
            onClick={() => navigate('/programmation-ia')}
            className="mb-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 transition hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour Programmation IA
          </button>
          <div className="flex flex-wrap items-start gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-3">
              <Server className="h-8 w-8 text-indigo-200" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl font-black text-white md:text-4xl">
                Procédure d’installation OpenClaw
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-300 md:text-base">
                Guide pas à pas : prérequis comptes, Ubuntu Server sur GMKtec, Tailscale, Incus
                (conteneurs agents) et configuration du pool / profils.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="border-b border-indigo-500/20 bg-slate-900/80 p-4 lg:sticky lg:top-0 lg:h-[calc(100vh-1px)] lg:overflow-y-auto lg:border-b-0 lg:border-r">
          <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Menu de procédure
          </p>
          <nav className="space-y-1.5">
            {sections.map((section) => {
              const Icon = section.icon;
              const active = section.id === activeId;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveId(section.id)}
                  className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                    active
                      ? 'border-indigo-500 bg-indigo-600 text-white shadow-lg'
                      : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-black ${
                      active ? 'bg-white/20' : 'bg-slate-800 text-indigo-200'
                    }`}
                  >
                    {section.number}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      <span className="truncate">{section.shortLabel}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="min-h-0 space-y-6 p-4 md:p-8">
          <div className="rounded-3xl border border-indigo-400/20 bg-indigo-500/10 p-5">
            <h2 className="text-xl font-black text-white">Étape active</h2>
            <p className="mt-1 text-sm text-slate-300">
              {activeSection.number}. {activeSection.label}
            </p>
          </div>

          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = section.id === activeId;
            return (
              <section
                key={section.id}
                id={`section-${section.id}`}
                className={`scroll-mt-6 rounded-3xl border p-5 md:p-6 ${
                  isActive
                    ? 'border-indigo-400/40 bg-white/10 shadow-[0_0_0_1px_rgba(99,102,241,0.15)]'
                    : 'border-white/10 bg-white/5'
                }`}
              >
                <div className="mb-4 flex items-start gap-3">
                  <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/15 p-3">
                    <Icon className="h-5 w-5 text-indigo-200" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-indigo-300">
                      Étape {section.number}
                    </div>
                    <h3 className="text-2xl font-black text-white">{section.label}</h3>
                    {section.intro ? (
                      <p className="mt-2 text-sm leading-7 text-slate-300">{section.intro}</p>
                    ) : null}
                  </div>
                </div>

                <ul className="space-y-2.5">
                  {section.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 rounded-2xl border border-white/5 bg-slate-950/40 px-4 py-3 text-sm leading-6 text-slate-200"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {section.commands?.length ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Commandes
                    </p>
                    {section.commandsCallout ? (
                      <div className="relative overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 p-[2px] shadow-[0_0_28px_rgba(251,191,36,0.55)]">
                        <div className="flex items-center gap-3 rounded-[14px] bg-slate-950 px-4 py-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-slate-950 shadow-[0_0_18px_rgba(251,191,36,0.7)]">
                            <AlertTriangle className="h-6 w-6" strokeWidth={2.5} />
                          </div>
                          <p className="text-base font-black uppercase tracking-wide text-amber-300 md:text-lg">
                            {section.commandsCallout}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    {section.commands.map((entry) => {
                      const cmd = normalizeCommand(entry);
                      return (
                        <CommandBlock
                          key={`${cmd.label ?? ''}-${cmd.command}`}
                          label={cmd.label}
                          command={cmd.command}
                          highlightNames={cmd.highlightNames}
                        />
                      );
                    })}
                  </div>
                ) : null}

                {section.exampleOutput ? (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Exemple de résultat (incus list)
                    </p>
                    <div className="overflow-x-auto rounded-2xl border border-cyan-400/25 bg-[#0b1220] shadow-[inset_0_0_40px_rgba(34,211,238,0.06)]">
                      <pre className="min-w-max px-4 py-4 font-mono text-[11px] leading-5 text-cyan-100 md:text-xs">
                        {section.exampleOutput}
                      </pre>
                    </div>
                    <p className="text-xs text-slate-400">
                      Tes noms et adresses IP seront différents — l’important est que tes conteneurs
                      apparaissent en <span className="font-bold text-emerald-300">RUNNING</span>.
                    </p>
                  </div>
                ) : null}

                {section.expected ? (
                  <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                    <strong className="font-bold">Résultat attendu :</strong> {section.expected}
                  </div>
                ) : null}

                {section.note ? (
                  <div className="mt-4 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                    {section.note}
                  </div>
                ) : null}

                {section.links?.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {section.links.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/20"
                      >
                        {link.label}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}

          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-sm text-slate-300">
            Tu peux revenir à la gouvernance OpenClaw depuis{' '}
            <Link to="/programmation-ia" className="font-semibold text-indigo-300 hover:text-indigo-200">
              Programmation IA
            </Link>
            .
          </div>
        </main>
      </div>
    </div>
  );
}
